import { Flex, Stack } from '@mantine/core';
import type { Initiative } from '../../../api/initiative-client/initiative-client.type';
import type { Goal, Outcome } from '../../../api/strategy-client/strategy-client.type';
import { useTranslation } from 'react-i18next';
import { OverviewCard } from '../../../components/overview-card/overview-card';
import { ProgressBarCard } from '../../../components/overview-card/progress-bar-card';
import { graphCambridgeBlue, purpleBase, skyDark } from '../../../styles/design-tokens';
import { H3, Text } from '../../../ui-library/typography/typography';
import { formatInitiativeDate } from '../strategy.helpers';

type EntityType = 'initiative' | 'outcome' | 'goal';

type Props = {
  entity: Initiative | Outcome | Goal | null;
  entityType: EntityType;
};

// Type guard for initiatives
function isInitiative(entity: Initiative | Outcome | Goal): entity is Initiative {
  return 'portfolio' in entity;
}

export const StrategyPerformanceOverview = ({ entity, entityType }: Props) => {
  const i18n = useTranslation();
  if (!entity) return null;

  const getTitle = () => {
    switch (entityType) {
      case 'goal':
        return `${i18n.t('common.strategic_goal')} Completion`;
      case 'outcome':
        return `${i18n.t('common.outcome')} Completion`;
      default:
        return i18n.t('strategy.initiative_performance.initiative_completion');
    }
  };

  const getCompletedTasksCount = () => {
    const completedTasks = (entity as any).completed_subtasks_over_time?.tasks || {};
    const taskCounts = Object.values(completedTasks).filter((count): count is number => count !== null);
    return taskCounts.length > 0 ? Math.max(...taskCounts) : 0;
  };

  const percentProgress = (entity as any).percent_progress || 0;
  const percentElapsed = (entity as any).percent_elapsed || 0;

  // Get date fields - initiatives use effective_start_date, outcomes/goals use activity_start_date
  const startDate = entity.start_date;
  const endDate = entity.end_date;
  const activityStartDate = isInitiative(entity)
    ? entity.effective_start_date
    : (entity as Outcome | Goal).activity_start_date;
  const activityEndDate = isInitiative(entity)
    ? entity.effective_end_date
    : (entity as Outcome | Goal).activity_end_date;

  // Get costs - only initiatives have costs_to_date currently
  let costToDate = 0;
  if (isInitiative(entity)) {
    costToDate = entity.costs_to_date?.initiative || 0;
  } else if (entityType === 'outcome') {
    costToDate = (entity as Outcome).costs_to_date?.outcome || 0;
  } else if (entityType === 'goal') {
    costToDate = (entity as Goal).costs_to_date?.goal || 0;
  }

  return (
    <Flex direction="row" gap={24}>
      <ProgressBarCard
        title={getTitle()}
        icon="check_circle"
        status={
          <Flex direction="column">
            <Flex justify="space-between">
              <Text size="tiny" color={skyDark}>
                {i18n.t('strategy.initiative_performance.start_date')}
              </Text>
              <Text size="tiny">{formatInitiativeDate(startDate, i18n.t)}</Text>
            </Flex>
            <Flex justify="space-between">
              <Text size="tiny" color={skyDark}>
                {i18n.t('strategy.initiative_performance.end_date')}
              </Text>
              <Text size="tiny">{formatInitiativeDate(endDate, i18n.t)}</Text>
            </Flex>
            {activityStartDate && startDate !== activityStartDate && (
              <Flex justify="space-between">
                <Text size="tiny" color={skyDark}>
                  {i18n.t('strategy.initiative_performance.activity_start_date')}
                </Text>
                <Text size="tiny">{formatInitiativeDate(activityStartDate, i18n.t)}</Text>
              </Flex>
            )}
            {activityEndDate && endDate !== activityEndDate && (
              <Flex justify="space-between">
                <Text size="tiny" color={skyDark}>
                  {i18n.t('strategy.initiative_performance.activity_end_date')}
                </Text>
                <Text size="tiny">{formatInitiativeDate(activityEndDate, i18n.t)}</Text>
              </Flex>
            )}
          </Flex>
        }
        value={percentProgress}
        color={graphCambridgeBlue}
      />
      <ProgressBarCard
        title={i18n.t('strategy.initiative_performance.time_elapsed')}
        icon="check_circle"
        value={percentElapsed}
        color={purpleBase}
      />
      <OverviewCard
        title={i18n.t('strategy.initiative_performance.cost_to_date')}
        icon="payments"
        content={
          <Flex align="center" justify="center" h="100%">
            <H3>${costToDate.toLocaleString()}</H3>
          </Flex>
        }
      />
      <OverviewCard
        title={i18n.t('strategy.initiative_performance.throughput')}
        icon="show_chart"
        content={
          <Stack justify="center" align="center" gap={0} mt={10}>
            <H3>{getCompletedTasksCount()}</H3>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              {i18n.t('strategy.overview.tasks_completed', { work_units: i18n.t('common.work_units') })}
            </Text>
            <Text size="tiny" lineHeight="tight" color={skyDark}>
              {i18n.t('strategy.overview.all_time')}
            </Text>
          </Stack>
        }
      />
    </Flex>
  );
};
